import React, { useState } from 'react';
import { Steps, Button, Alert, Input, Form, Typography, Space, Checkbox } from 'antd';
import { ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/trpc';
import { useAuthStore } from '../../stores/authStore';
import { withDevModalFilePath } from '../common';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Modal } = withDevModalFilePath('client/src/components/company/WithdrawModal.tsx');

interface WithdrawModalProps {
  open: boolean;
  onClose: () => void;
  companyName: string;
}

const WithdrawModal: React.FC<WithdrawModalProps> = ({ open, onClose, companyName }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [form] = Form.useForm();
  const [confirmed, setConfirmed] = useState(false);
  const clearAuth = useAuthStore((state) => state.clearAuth);

  const withdrawMutation = trpc.company.withdraw.useMutation({
    onSuccess: () => {
      // 退会成功後、認証情報をクリアしてログインページへリダイレクト
      clearAuth();
      // persist storageもクリア
      localStorage.removeItem('auth-storage');
      // ページをリロードしてログインページへ
      window.location.replace('/login?withdrawn=true');
    },
    onError: (error) => {
      Modal.error({
        title: '退会処理に失敗しました',
        content: error.message,
      });
    },
  });

  const handleNext = () => {
    if (currentStep === 0 && confirmed) {
      setCurrentStep(1);
    }
  };

  const handleBack = () => {
    setCurrentStep(0);
  };

  const handleWithdraw = async () => {
    try {
      const values = await form.validateFields();

      if (values.companyName !== companyName) {
        form.setFields([
          {
            name: 'companyName',
            errors: ['会社名が一致しません'],
          },
        ]);
        return;
      }

      withdrawMutation.mutate({
        confirmCompanyName: values.companyName,
        reason: values.reason,
      });
    } catch {
      // バリデーションエラー
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setConfirmed(false);
    form.resetFields();
    onClose();
  };

  const steps = [
    {
      title: '注意事項の確認',
      content: (
        <div>
          <Alert
            message="退会すると以下の影響があります"
            description={
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>
                  <Text strong>全ユーザーがログインできなくなります</Text>
                  <br />
                  <Text type="secondary">この会社に所属する全てのユーザーアカウントが無効化されます</Text>
                </li>
                <li style={{ marginTop: 8 }}>
                  <Text strong>データは削除されます</Text>
                  <br />
                  <Text type="secondary">退会後、アカウントデータは削除されます</Text>
                </li>
                <li style={{ marginTop: 8 }}>
                  <Text strong>この操作は取り消せません</Text>
                  <br />
                  <Text type="secondary">再度利用する場合は新規登録が必要です</Text>
                </li>
              </ul>
            }
            type="warning"
            showIcon
            icon={<WarningOutlined />}
          />

          <div style={{ marginTop: 24 }}>
            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            >
              <Text>上記の内容を理解し、退会手続きを続行します</Text>
            </Checkbox>
          </div>
        </div>
      ),
    },
    {
      title: '最終確認',
      content: (
        <div>
          <Alert
            message={
              <Space>
                <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                <Text strong style={{ color: '#ff4d4f' }}>最終確認</Text>
              </Space>
            }
            description={
              <Paragraph style={{ marginBottom: 0 }}>
                退会を確定するには、会社名を入力してください。
              </Paragraph>
            }
            type="error"
          />

          <Form
            form={form}
            layout="vertical"
            style={{ marginTop: 24 }}
          >
            <Form.Item
              name="companyName"
              label={
                <span>
                  会社名を入力してください: <Text strong>{companyName}</Text>
                </span>
              }
              rules={[
                { required: true, message: '会社名を入力してください' },
              ]}
            >
              <Input placeholder="会社名を入力" />
            </Form.Item>

            <Form.Item
              name="reason"
              label="退会理由（任意）"
            >
              <TextArea
                rows={3}
                placeholder="今後のサービス改善のため、よろしければ退会理由をお聞かせください"
              />
            </Form.Item>
          </Form>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <ExclamationCircleOutlined style={{ color: '#faad14' }} />
          <span>退会手続き</span>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      width={600}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={handleClose}>
            キャンセル
          </Button>
          <Space>
            {currentStep > 0 && (
              <Button onClick={handleBack}>
                戻る
              </Button>
            )}
            {currentStep === 0 ? (
              <Button
                type="primary"
                onClick={handleNext}
                disabled={!confirmed}
              >
                次へ
              </Button>
            ) : (
              <Button
                type="primary"
                danger
                onClick={handleWithdraw}
                loading={withdrawMutation.isPending}
              >
                退会を確定する
              </Button>
            )}
          </Space>
        </div>
      }
    >
      <Steps
        current={currentStep}
        items={steps.map((step) => ({ title: step.title }))}
        style={{ marginBottom: 24 }}
      />

      {steps[currentStep].content}
    </Modal>
  );
};

export default WithdrawModal;
